import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bucket, GetSalesInsightsAggregatedRequest, SalesInsightsGroupedAggregate, SalesInsightsGroupedAggregateItem, useSalesInsightsGroupedAggregated } from '../../../../api/indicators';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { dateRange, formatDateParam } from '../../../../helpers/dateUtils';
import LineBarChart, { LineBarChartDataItem } from '../charts/lineBarChart/LineBarChart';
import { DashboardContext } from '../../DashboardContext';
import { SettingsContext } from '../../../../context/SettingsContext';
import { getBucketFromRange } from '../../dashboardHelpers';

const NetSalesByLocation = () => {
    const { t } = useTranslation();
    const { calendarPreferences } = useContext(SettingsContext);
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { refreshDate } = useContext(DashboardContext);

    const initialStartDate = useMemo(() => filters.periodFrom
        ? filters.periodFrom
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate, [filters.periodFrom]);

    const initialEndDate = useMemo(() => filters.periodTo
        ? filters.periodTo
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate, [filters.periodTo]);


    const [params, setParams] = useState<GetSalesInsightsAggregatedRequest>({
        fields: 'transactionVolume,netSales',
        startDate: formatDateParam(initialStartDate),
        endDate: formatDateParam(initialEndDate),
        locations: filters?.locations?.join(','),
        locationArea: filters?.locationArea?.id,
        businessStartHour: calendarPreferences.daysStart,
        bucket: getBucketFromRange(initialStartDate, initialEndDate),
        groupBy: 'location'
    });

    const {
        data: netSalesByLocationResponse,
        isLoading,
        isError,
        refetch: fetchSalesInsightsGroupedAggregate
    } = useSalesInsightsGroupedAggregated<SalesInsightsGroupedAggregate>(params, 'sales-insights/grouped-aggregate')

    useEffect(() => {

        const start = filters.periodFrom
            ? filters.periodFrom
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate;

        const end = filters.periodTo
            ? filters.periodTo
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate;

        setParams(p => ({
            ...p,
            startDate: formatDateParam(start),
            endDate: formatDateParam(end),
            locations: filters?.locations?.join(','),
            locationArea: filters?.locationArea?.id,
            bucket: getBucketFromRange(start, end)
        }));
    }, [filters, dateRangeConfig]);

    useEffect(() => { fetchSalesInsightsGroupedAggregate() }, [params, fetchSalesInsightsGroupedAggregate]);

    useEffect(() => {
        if (refreshDate != null) {
            fetchSalesInsightsGroupedAggregate();
        }
    }, [refreshDate, fetchSalesInsightsGroupedAggregate]);

    function transformData(dataItem: SalesInsightsGroupedAggregateItem): LineBarChartDataItem {
        const result: LineBarChartDataItem = {
            dateTime: new Date(dataItem.dateTime),
            barData: 0
        };

        dataItem.salesInsightsGrouped.forEach(({ key, salesInsights }) => {
            result[key] = salesInsights.netSalesExcTax ?? 0;
            result.barData += salesInsights.transactionVolume ?? 0;
        });
        return result;
    }

    return (
        <LineBarChart
            title={t("screens.dashboarding.charts.netSalesByLocation")}
            currency={netSalesByLocationResponse?.currency}
            description={t("screens.dashboarding.charts.excludingTax")}
            data={netSalesByLocationResponse?.salesInsightsGroupedAggregate?.map(transformData) ?? []}
            isLoading={isLoading}
            isError={isError}
            leftAxisLabel={t("screens.dashboarding.charts.amount")}
            rightAxisLabel={t("screens.dashboarding.charts.volume")}
            barLabel={t('screens.dashboarding.charts.volume')}
            bucket={params.bucket as Bucket}
            setBucket={(newView: Bucket) => setParams({ ...params, bucket: newView })}
            height={460}
        />
    )
}

export default NetSalesByLocation;
